import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useApi } from '../../hooks/useApi';
import logger from '../../utils/logger';
import { fmtDate, fmtDateTime } from '../../utils/ruDates';

export default function AccountDataPanel() {
  const { logout } = useAuth();
  const { request, showStatus } = useApi();
  const [busy, setBusy] = useState(false);
  const [deletionStatus, setDeletionStatus] = useState(null);

  const load = async () => {
    try {
      const status = await request('/api/v1/users/me/delete-status');
      setDeletionStatus(status || null);
    } catch (error) {
      logger.warn('Could not load account deletion status:', error);
    }
  };

  useEffect(() => { load(); }, []);

  const handleExportData = async () => {
    setBusy(true);
    try {
      const data = await request('/api/v1/users/me/export');
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'my-data.json';
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      logger.error(error);
      showStatus('error', 'Не удалось выгрузить данные.');
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deletionStatus && ['requested', 'confirmed', 'in_progress'].includes(deletionStatus.status)) {
      showStatus('error', 'Запрос на удаление уже подан.');
      return;
    }
    const phrase = window.prompt('Для подтверждения введите DELETE');
    if (phrase !== 'DELETE') {
      showStatus('error', 'Подтверждение не выполнено.');
      return;
    }
    setBusy(true);
    try {
      await request('/api/v1/users/me/delete-request', { method: 'POST' });
      const confirmed = await request('/api/v1/users/me/delete-confirm', { method: 'POST' });
      setDeletionStatus(confirmed);
      showStatus('success', 'Запрос на удаление подтверждён. Сессия будет завершена.');
      logout();
      window.location.href = '/';
    } catch (error) {
      logger.error(error);
      showStatus('error', 'Не удалось отправить запрос на удаление аккаунта.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="panel">
      <div className="panel__head">
        <h3>Управление данными аккаунта</h3>
        <button type="button" className="ghost" onClick={load} disabled={busy}>Обновить</button>
      </div>
      <p className="muted">Выгрузка персональных данных и удаление аккаунта.</p>
      {deletionStatus && (
        <div className="billing-summary" style={{ marginBottom: 12 }}>
          <div className="billing-stat">
            <span className="muted">Статус</span>
            <strong>{deletionStatus.status}</strong>
          </div>
          <div className="billing-stat">
            <span className="muted">Запрошено</span>
            <strong>{deletionStatus.requested_at ? fmtDateTime(deletionStatus.requested_at) : '—'}</strong>
          </div>
          <div className="billing-stat">
            <span className="muted">Плановая очистка</span>
            <strong>{deletionStatus.scheduled_purge_at ? fmtDate(deletionStatus.scheduled_purge_at) : '—'}</strong>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" onClick={handleExportData} disabled={busy}>Скачать мои данные</button>
        <button type="button" className="ghost" onClick={handleDeleteAccount} disabled={busy}>
          Удалить мой аккаунт
        </button>
      </div>
      <p className="muted" style={{ marginTop: 12 }}>
        После подтверждения удаления часть данных может храниться в течение сроков, требуемых законодательством РФ.
      </p>
    </div>
  );
}
